import React from "react";
import { observer } from "mobx-react";
import { PublisherUpdateStore } from "../../store";
import { PublisherUpdateForm } from "./PublisherUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { PersonType } from "../../../../api/graphql";
import { MaritalStatus } from "../../../../api/graphql";

export type PublisherUpdateProps = {
  publisherUpdateStore: PublisherUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const PublisherUpdate: React.FC<PublisherUpdateProps> = observer(
  ({ publisherUpdateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={publisherUpdateStore.isSaving} />
        <OutdatedAfterSaveNotice
          open={publisherUpdateStore.savedButOutdated}
          onReload={() => publisherUpdateStore.reloadPublisher()}
          reloadDisabled={publisherUpdateStore.isProcessing}
        />
        <PublisherUpdateForm
          publisherUpdateStore={publisherUpdateStore}
          personTypeError={publisherUpdateStore.errors.personType?.message}
          personTypeOptions={publisherUpdateStore.getPersonTypeOptions()}
          onPersonTypeChange={(value: PersonType) => {
            publisherUpdateStore.setPersonTypeValue(value);
          }}
          personType={publisherUpdateStore.entityValues.personType}
          nameError={publisherUpdateStore.errors.name?.message}
          onNameChange={(value: string) => {
            publisherUpdateStore.setNameValue(value);
          }}
          name={publisherUpdateStore.entityValues.name}
          maritalStatusError={
            publisherUpdateStore.errors.maritalStatus?.message
          }
          maritalStatusOptions={publisherUpdateStore.getMaritalStatusOptions()}
          onMaritalStatusChange={(value: MaritalStatus | "") => {
            publisherUpdateStore.setMaritalStatusValue(value);
          }}
          maritalStatus={publisherUpdateStore.entityValues.maritalStatus}
          nationalityError={publisherUpdateStore.errors.nationality?.message}
          onNationalityChange={(value) => {
            publisherUpdateStore.setNationalityValue(value);
          }}
          nationalitySelectedValue={
            publisherUpdateStore.entityValues.showNationality
          }
          onNationalitySelected={(value) => {
            publisherUpdateStore.setSelectedNationality(value);
          }}
          nationalityOptions={publisherUpdateStore.getNationalityOptions()}
          pseudonymError={publisherUpdateStore.errors.pseudonym?.message}
          onPseudonymChange={(value: string) => {
            publisherUpdateStore.setPseudonymValue(value);
          }}
          pseudonym={publisherUpdateStore.entityValues.pseudonym}
          taxIdError={publisherUpdateStore.errors.taxId?.message}
          onTaxIdChange={(value: string) => {
            publisherUpdateStore.setTaxIdValue(value);
          }}
          taxId={publisherUpdateStore.entityValues.taxId}
          phoneError={publisherUpdateStore.errors.phone?.message}
          onPhoneChange={(value: string) => {
            publisherUpdateStore.setPhoneValue(value);
          }}
          phone={publisherUpdateStore.entityValues.phone}
          emailError={publisherUpdateStore.errors.email?.message}
          onEmailChange={(value: string) => {
            publisherUpdateStore.setEmailValue(value);
          }}
          email={publisherUpdateStore.entityValues.email}
          streetAndNumberError={
            publisherUpdateStore.errors.streetAndNumber?.message
          }
          onStreetAndNumberChange={(value: string) => {
            publisherUpdateStore.setStreetAndNumberValue(value);
          }}
          streetAndNumber={publisherUpdateStore.entityValues.streetAndNumber}
          floorError={publisherUpdateStore.errors.floor?.message}
          onFloorChange={(value: string) => {
            publisherUpdateStore.setFloorValue(value);
          }}
          floor={publisherUpdateStore.entityValues.floor}
          suiteError={publisherUpdateStore.errors.suite?.message}
          onSuiteChange={(value: string) => {
            publisherUpdateStore.setSuiteValue(value);
          }}
          suite={publisherUpdateStore.entityValues.suite}
          postalCodeError={publisherUpdateStore.errors.postalCode?.message}
          onPostalCodeChange={(value: string) => {
            publisherUpdateStore.setPostalCodeValue(value);
          }}
          postalCode={publisherUpdateStore.entityValues.postalCode}
          countryError={publisherUpdateStore.errors.country?.message}
          onCountryChange={(value) => {
            publisherUpdateStore.setCountryValue(value);
          }}
          countrySelectedValue={publisherUpdateStore.entityValues.showCountry}
          onCountrySelected={(value) => {
            publisherUpdateStore.setSelectedCountry(value);
          }}
          countryOptions={publisherUpdateStore.getCountryOptions()}
          provinceError={publisherUpdateStore.errors.province?.message}
          onProvinceChange={(value) => {
            publisherUpdateStore.setProvinceValue(value);
          }}
          provinceSelectedValue={publisherUpdateStore.entityValues.showProvince}
          onProvinceSelected={(value) => {
            publisherUpdateStore.setSelectedProvince(value);
          }}
          provinceOptions={publisherUpdateStore.getProvinceOptions()}
          cityError={publisherUpdateStore.errors.city?.message}
          onCityChange={(value: string) => {
            publisherUpdateStore.setCityValue(value);
          }}
          city={publisherUpdateStore.entityValues.city}
          caeIpiError={publisherUpdateStore.errors.caeIpi?.message}
          onCaeIpiChange={(value: string) => {
            publisherUpdateStore.setCaeIpiValue(value);
          }}
          caeIpi={publisherUpdateStore.entityValues.caeIpi}
          publisherRepresentativeUpdateStore={
            publisherUpdateStore.representativesUpdateStore
          }
          representativesVisibleColumns={
            publisherUpdateStore.representativesVisibleColumns
          }
          representativesColumnTypes={
            publisherUpdateStore.representativesColumnTypes
          }
          representativesOnRestore={(id: string) =>
            publisherUpdateStore.representativesRestore(id)
          }
          representativesOnCommitChanges={(changes) =>
            publisherUpdateStore.representativesCommitChanges(changes)
          }
          representativesOnCommitInlineChange={(rowId, changes) =>
            publisherUpdateStore.representativesCommitInlineChange(
              rowId,
              changes
            )
          }
          representativesValidateChanges={(changes) =>
            publisherUpdateStore.representativesValidateChanges(changes)
          }
          representativesRows={
            publisherUpdateStore.entityValues.showRepresentatives
          }
          representativesError={
            publisherUpdateStore.errors.representatives?.message
          }
          publisherCollectingSocietyUpdateStore={
            publisherUpdateStore.managementSocietiesUpdateStore
          }
          managementSocietiesVisibleColumns={
            publisherUpdateStore.managementSocietiesVisibleColumns
          }
          managementSocietiesColumnTypes={
            publisherUpdateStore.managementSocietiesColumnTypes
          }
          managementSocietiesOnRestore={(id: string) =>
            publisherUpdateStore.managementSocietiesRestore(id)
          }
          managementSocietiesOnCommitChanges={(changes) =>
            publisherUpdateStore.managementSocietiesCommitChanges(changes)
          }
          managementSocietiesOnCommitInlineChange={(rowId, changes) =>
            publisherUpdateStore.managementSocietiesCommitInlineChange(
              rowId,
              changes
            )
          }
          managementSocietiesValidateChanges={(changes) =>
            publisherUpdateStore.managementSocietiesValidateChanges(changes)
          }
          managementSocietiesRows={
            publisherUpdateStore.entityValues.showManagementSocieties
          }
          managementSocietiesError={
            publisherUpdateStore.errors.managementSocieties?.message
          }
          createdAt_={publisherUpdateStore.getCreatedAt_Value()}
          lastUpdatedAt_={publisherUpdateStore.getLastUpdatedAt_Value()}
          dummyCRUDRestoredObservable={
            publisherUpdateStore.dummyCRUDRestoredObservable
          }
          recordId={publisherUpdateStore.publisherInstance?.id || ""}
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={publisherUpdateStore.saveDisabled}
          onSave={() => publisherUpdateStore.savePublisher()}
          onCancel={() => publisherUpdateStore.cancelPublisher()}
        />
        <ErrorDialog
          open={publisherUpdateStore.errorDialogOpened}
          title={publisherUpdateStore.errorTitle}
          description={publisherUpdateStore.errorDescription}
          onClose={() => publisherUpdateStore.closeErrorDialog()}
        />
        <ConfirmCancelDialog
          open={publisherUpdateStore.confirmDialogOpened}
          title={publisherUpdateStore.confirmDialogTitle}
          description={publisherUpdateStore.confirmDialogDescription}
          onConfirm={() => publisherUpdateStore.executeConfirmedAction()}
          onCancel={() => publisherUpdateStore.closeConfirmDialog()}
        />
      </>
    );
  }
);
